import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiMessageSquare, FiFolderPlus, FiCode, FiEye, FiCloud, FiSettings, FiZap, FiTarget, FiDollarSign } from 'react-icons/fi';
import ScrollHeader from '../components/ScrollHeader';
import ImageModal from '../components/ImageModal';

const GettingStarted: React.FC = () => {
  const [selectedImage, setSelectedImage] = React.useState<string | null>(null);

  const features = [
    {
      icon: <FiFolderPlus className="w-8 h-8" />,
      title: "Projects",
      description: "Organize your infrastructure work",
      link: "/projects"
    },
    {
      icon: <FiMessageSquare className="w-8 h-8" />,
      title: "Chat with Ana",
      description: "Generate code from natural language",
      link: "/chat"
    },
    {
      icon: <FiCode className="w-8 h-8" />,
      title: "Code Editor",
      description: "Review and edit generated code",
      link: "/code-editor"
    },
    {
      icon: <FiEye className="w-8 h-8" />,
      title: "Visualisation",
      description: "See and deploy infrastructure visually",
      link: "/visualisation"
    },
    {
      icon: <FiCloud className="w-8 h-8" />,
      title: "Cloud Analyzer",
      description: "Optimize existing infrastructure",
      link: "/cloud-analyzer"
    },
    {
      icon: <FiSettings className="w-8 h-8" />,
      title: "Integrations",
      description: "Connect your cloud providers",
      link: "/integrations"
    }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <ScrollHeader title="Getting Started with Ana DevOps" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="prose prose-lg dark:prose-invert max-w-none">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <p className="text-2xl text-gray-600 dark:text-gray-300 mb-6">
              Your AI-powered infrastructure automation platform
            </p>
            <p className="text-lg text-gray-500 dark:text-gray-400">
              Transform natural language into production-ready infrastructure code
            </p>
          </div>

          <div 
            className="my-8 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700 cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
            onClick={() => setSelectedImage('/screenshots/authentication/01_login-page.png')}
          >
            <img 
              src="/screenshots/authentication/01_login-page.png" 
              alt="Ana Login Page" 
              className="w-full"
            />
          </div>

          {/* Quick Start */}
          <div className="bg-gradient-to-br from-primary-50 to-orange-50 dark:from-primary-900/20 dark:to-orange-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-0 mb-6">
              🚀 Quick Start (5 Minutes)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white dark:bg-gray-900 p-4 rounded-lg">
                <div className="text-3xl font-bold text-primary-500 mb-2">1</div>
                <h3 className="text-lg font-semibold mb-2 mt-0">Create Project</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                  Set up your first infrastructure project
                </p>
              </div>
              <div className="bg-white dark:bg-gray-900 p-4 rounded-lg">
                <div className="text-3xl font-bold text-primary-500 mb-2">2</div>
                <h3 className="text-lg font-semibold mb-2 mt-0">Chat with Ana</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                  Describe what you need in plain English
                </p>
              </div>
              <div className="bg-white dark:bg-gray-900 p-4 rounded-lg">
                <div className="text-3xl font-bold text-primary-500 mb-2">3</div>
                <h3 className="text-lg font-semibold mb-2 mt-0">Deploy</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                  Review, visualize, and deploy your infrastructure
                </p>
              </div>
            </div>
          </div>

          {/* Platform Tour */}
          <h2>Take a Tour of Ana DevOps</h2>
          <p>
            Explore the platform's key features and see how Ana helps you build infrastructure faster:
          </p>

          <div className="space-y-6 my-8">
            <div 
              className="rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700 cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
              onClick={() => setSelectedImage('/screenshots/dashboard/take_tour_1.png')}
            >
              <img 
                src="/screenshots/dashboard/take_tour_1.png" 
                alt="Ana Platform Tour - Part 1" 
                className="w-full"
              />
              <div className="p-4 bg-gray-50 dark:bg-gray-800">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                  <strong>Platform Overview:</strong> Navigate through projects, chat with Ana, and access all features from the intuitive dashboard
                </p>
              </div>
            </div>

            <div 
              className="rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700 cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
              onClick={() => setSelectedImage('/screenshots/dashboard/take_tour_2.png')}
            >
              <img 
                src="/screenshots/dashboard/take_tour_2.png" 
                alt="Ana Platform Tour - Part 2" 
                className="w-full"
              />
              <div className="p-4 bg-gray-50 dark:bg-gray-800">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                  <strong>Workflow in Action:</strong> See how code generation, visualization, and deployment work together seamlessly
                </p>
              </div>
            </div>
          </div>

          {/* Core Features */}
          <h2>Core Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
            {features.map((feature, index) => (
              <Link
                key={index}
                to={feature.link}
                className="no-underline"
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="bg-light-surface dark:bg-dark-surface p-6 rounded-lg border border-light-border dark:border-dark-border hover:border-primary-500 dark:hover:border-primary-400 transition-all hover:shadow-lg group"
                >
                  <div className="text-primary-500 mb-4 group-hover:scale-110 transition-transform">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mt-0 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-0">
                    {feature.description}
                  </p>
                </motion.div>
              </Link>
            ))}
          </div>

          {/* Why Ana DevOps */}
          <h2>Why Ana DevOps?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-8">
            <div className="text-center p-6 bg-light-surface dark:bg-dark-surface rounded-lg border border-light-border dark:border-dark-border">
              <div className="text-primary-500 mb-4 flex justify-center">
                <FiZap className="w-12 h-12" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Lightning Fast</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                Generate complete infrastructure code in seconds instead of hours of manual work
              </p>
            </div>
            <div className="text-center p-6 bg-light-surface dark:bg-dark-surface rounded-lg border border-light-border dark:border-dark-border">
              <div className="text-primary-500 mb-4 flex justify-center">
                <FiTarget className="w-12 h-12" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Production-Ready</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                Code follows industry best practices, security standards, and is ready to deploy
              </p>
            </div>
            <div className="text-center p-6 bg-light-surface dark:bg-dark-surface rounded-lg border border-light-border dark:border-dark-border">
              <div className="text-primary-500 mb-4 flex justify-center">
                <FiDollarSign className="w-12 h-12" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Cost-Aware</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                Preview infrastructure costs before deployment to stay within budget
              </p>
            </div>
          </div>

          {/* How It Works */}
          <h2>How It Works</h2>
          <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg mb-8">
            <ol className="space-y-4 mb-0">
              <li>
                <strong>Describe:</strong> Tell Ana what infrastructure you need in plain English - no need to know Terraform syntax
              </li>
              <li>
                <strong>Generate:</strong> Ana creates complete Terraform code, Kubernetes manifests, or other IaC with best practices built-in
              </li>
              <li>
                <strong>Visualize:</strong> See your infrastructure as an interactive diagram to understand the architecture
              </li>
              <li>
                <strong>Deploy:</strong> Preview costs, review the plan, and deploy with confidence to AWS, Azure, or GCP
              </li>
            </ol>
          </div>

          {/* Example Use Case */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-6 rounded-r-lg mb-8">
            <h3 className="text-blue-800 dark:text-blue-400 mt-0 mb-3">💡 Real Example</h3>
            <p className="text-blue-700 dark:text-blue-300 mb-3 font-medium">
              You say:
            </p>
            <p className="text-blue-700 dark:text-blue-300 mb-3 italic bg-blue-100 dark:bg-blue-900/30 p-3 rounded">
              "Create a web application on AWS with an EC2 instance, PostgreSQL database, 
              S3 bucket for assets, and a load balancer with SSL"
            </p>
            <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
              <strong>→ Ana generates:</strong> Complete Terraform code with VPC, subnets, security groups, 
              RDS instance, S3 bucket with proper policies, Application Load Balancer with ACM certificate, 
              and all networking configured following AWS best practices.
            </p>
          </div>

          {/* What You Can Build */}
          <h2>What You Can Build with Ana</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-light-surface dark:bg-dark-surface p-5 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0 mb-3">🌐 Web Applications</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Full-stack infrastructure with compute, databases, CDN, and load balancing
              </p>
              <ul className="text-xs text-gray-500 dark:text-gray-500 space-y-1 mb-0">
                <li>• EC2/App Service/Compute Engine instances</li>
                <li>• RDS/Azure SQL/Cloud SQL databases</li>
                <li>• CloudFront/Azure CDN/Cloud CDN</li>
                <li>• Application Load Balancers</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-5 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0 mb-3">🔄 Microservices</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Kubernetes clusters with service mesh, monitoring, and CI/CD
              </p>
              <ul className="text-xs text-gray-500 dark:text-gray-500 space-y-1 mb-0">
                <li>• EKS/AKS/GKE cluster setup</li>
                <li>• Istio/Linkerd service mesh</li>
                <li>• Prometheus & Grafana monitoring</li>
                <li>• ArgoCD for GitOps</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-5 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0 mb-3">📊 Data Pipelines</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                ETL infrastructure with data lakes, processing, and analytics
              </p>
              <ul className="text-xs text-gray-500 dark:text-gray-500 space-y-1 mb-0">
                <li>• S3/Azure Data Lake/Cloud Storage</li>
                <li>• Glue/Data Factory/Dataflow</li>
                <li>• Redshift/Synapse/BigQuery</li>
                <li>• Airflow orchestration</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-5 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0 mb-3">⚡ Serverless APIs</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Event-driven architectures with managed services
              </p>
              <ul className="text-xs text-gray-500 dark:text-gray-500 space-y-1 mb-0">
                <li>• API Gateway/Azure API Management</li>
                <li>• Lambda/Functions/Cloud Functions</li>
                <li>• DynamoDB/Cosmos DB/Firestore</li>
                <li>• EventBridge/Event Grid/Pub/Sub</li>
              </ul>
            </div>
          </div>

          {/* Key Capabilities */}
          <h2>Key Capabilities</h2>
          
          <div className="space-y-6 my-8">
            <div className="bg-light-surface dark:bg-dark-surface p-6 rounded-lg border border-light-border dark:border-dark-border">
              <h3 className="text-primary-500 dark:text-primary-400 mt-0 mb-3">🤖 Natural Language to Code</h3>
              <p className="mb-3">
                No need to learn Terraform syntax or cloud-specific APIs. Just describe what you need:
              </p>
              <ul className="space-y-2 mb-0">
                <li>Conversational interface understands context and intent</li>
                <li>Iterative refinement through follow-up questions</li>
                <li>Explains decisions and suggests improvements</li>
                <li>Generates documentation alongside code</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-6 rounded-lg border border-light-border dark:border-dark-border">
              <h3 className="text-primary-500 dark:text-primary-400 mt-0 mb-3">👁️ Visual Infrastructure Design</h3>
              <p className="mb-3">
                See and edit your infrastructure as an interactive diagram:
              </p>
              <ul className="space-y-2 mb-0">
                <li>Node-based visual representation of all components</li>
                <li>Drag-and-drop to add new resources</li>
                <li>Real-time code synchronization</li>
                <li>Export diagrams for documentation</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-6 rounded-lg border border-light-border dark:border-dark-border">
              <h3 className="text-primary-500 dark:text-primary-400 mt-0 mb-3">💰 Cost Preview & Optimization</h3>
              <p className="mb-3">
                Know exactly what you'll pay before deploying:
              </p>
              <ul className="space-y-2 mb-0">
                <li>Per-resource cost breakdown</li>
                <li>Monthly and annual projections</li>
                <li>Cost optimization suggestions</li>
                <li>Compare different configuration options</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-6 rounded-lg border border-light-border dark:border-dark-border">
              <h3 className="text-primary-500 dark:text-primary-400 mt-0 mb-3">🔍 Infrastructure Analysis</h3>
              <p className="mb-3">
                Analyze and optimize existing infrastructure:
              </p>
              <ul className="space-y-2 mb-0">
                <li>Scan current cloud resources</li>
                <li>Identify cost-saving opportunities</li>
                <li>Detect security vulnerabilities</li>
                <li>Export existing infrastructure to code</li>
              </ul>
            </div>
          </div>

          {/* Supported Technologies */}
          <h2>Supported Technologies</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
            <div>
              <h3 className="text-lg font-semibold mb-3">Infrastructure as Code</h3>
              <ul className="space-y-2">
                <li>✅ Terraform (HCL)</li>
                <li>✅ AWS CloudFormation</li>
                <li>✅ Azure Resource Manager (ARM)</li>
                <li>✅ Pulumi</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Container Orchestration</h3>
              <ul className="space-y-2">
                <li>✅ Kubernetes (YAML manifests)</li>
                <li>✅ Docker Compose</li>
                <li>✅ Helm Charts</li>
                <li>✅ Kustomize</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Cloud Providers</h3>
              <ul className="space-y-2">
                <li>✅ Amazon Web Services (AWS)</li>
                <li>✅ Microsoft Azure</li>
                <li>✅ Google Cloud Platform (GCP)</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">CI/CD Integration</h3>
              <ul className="space-y-2">
                <li>✅ GitHub Actions</li>
                <li>✅ GitLab CI/CD</li>
                <li>✅ Jenkins</li>
                <li>✅ Azure DevOps</li>
              </ul>
            </div>
          </div>

          {/* Getting Started Steps */}
          <h2>Your First Deployment</h2>
          <p>
            Follow this workflow to deploy your first infrastructure with Ana:
          </p>

          <div className="space-y-6 my-8">
            <div className="bg-white dark:bg-gray-900 p-6 rounded-lg border-l-4 border-primary-500">
              <h3 className="text-primary-500 dark:text-primary-400 mt-0 mb-3">Step 1: Create a Project</h3>
              <ol className="space-y-2 mb-0">
                <li>Navigate to <Link to="/projects" className="text-primary-500 hover:text-primary-600">Projects</Link></li>
                <li>Click "Create Project"</li>
                <li>Enter name and description</li>
                <li>Select default cloud provider</li>
              </ol>
            </div>

            <div className="bg-white dark:bg-gray-900 p-6 rounded-lg border-l-4 border-primary-500">
              <h3 className="text-primary-500 dark:text-primary-400 mt-0 mb-3">Step 2: Generate Infrastructure</h3>
              <ol className="space-y-2 mb-0">
                <li>Go to <Link to="/chat" className="text-primary-500 hover:text-primary-600">Chat</Link></li>
                <li>Describe your infrastructure needs</li>
                <li>Review Ana's generated code</li>
                <li>Ask for modifications if needed</li>
              </ol>
            </div>

            <div className="bg-white dark:bg-gray-900 p-6 rounded-lg border-l-4 border-primary-500">
              <h3 className="text-primary-500 dark:text-primary-400 mt-0 mb-3">Step 3: Review & Visualize</h3>
              <ol className="space-y-2 mb-0">
                <li>Open <Link to="/code-editor" className="text-primary-500 hover:text-primary-600">Code Editor</Link> to review code</li>
                <li>Click eye button (👁️) to visualize</li>
                <li>Add or modify components visually</li>
                <li>Verify all connections and dependencies</li>
              </ol>
            </div>

            <div className="bg-white dark:bg-gray-900 p-6 rounded-lg border-l-4 border-primary-500">
              <h3 className="text-primary-500 dark:text-primary-400 mt-0 mb-3">Step 4: Set Up Integration</h3>
              <ol className="space-y-2 mb-0">
                <li>Navigate to <Link to="/integrations" className="text-primary-500 hover:text-primary-600">Integrations</Link></li>
                <li>Select your cloud provider (AWS/Azure/GCP)</li>
                <li>Enter credentials (IAM keys, Service Principal, etc.)</li>
                <li>Test connection and save</li>
              </ol>
            </div>

            <div className="bg-white dark:bg-gray-900 p-6 rounded-lg border-l-4 border-primary-500">
              <h3 className="text-primary-500 dark:text-primary-400 mt-0 mb-3">Step 5: Deploy</h3>
              <ol className="space-y-2 mb-0">
                <li>Return to Visualisation tool</li>
                <li>Click "Deploy" button</li>
                <li>Review deployment plan</li>
                <li>Check cost estimates</li>
                <li>Confirm and monitor deployment</li>
              </ol>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-6 rounded-r-lg mt-8">
            <h3 className="text-green-800 dark:text-green-400 mt-0 mb-4">🎯 Ready to Start?</h3>
            <div className="space-y-3">
              <div>
                <Link to="/projects" className="text-green-700 dark:text-green-300 font-semibold hover:text-green-600 text-lg">
                  1. Create Your First Project →
                </Link>
                <p className="text-sm text-green-600 dark:text-green-400 mb-0 ml-4 mt-1">
                  Start organizing your infrastructure work
                </p>
              </div>
              <div>
                <Link to="/chat" className="text-green-700 dark:text-green-300 font-semibold hover:text-green-600 text-lg">
                  2. Chat with Ana →
                </Link>
                <p className="text-sm text-green-600 dark:text-green-400 mb-0 ml-4 mt-1">
                  Describe what you want to build in plain English
                </p>
              </div>
              <div>
                <Link to="/integrations" className="text-green-700 dark:text-green-300 font-semibold hover:text-green-600 text-lg">
                  3. Set Up Cloud Integration →
                </Link>
                <p className="text-sm text-green-600 dark:text-green-400 mb-0 ml-4 mt-1">
                  Connect AWS, Azure, or GCP for deployment
                </p>
              </div>
            </div>
          </div>

          {/* Help Section */}
          <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-lg mt-8">
            <h3 className="mt-0 mb-4">Need Help?</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">📚 Explore Documentation</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                  Each feature has detailed guides with examples and best practices
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">💬 Ask Ana Anything</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                  Use the chat to ask questions about infrastructure, best practices, or troubleshooting
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <ImageModal
        isOpen={selectedImage !== null}
        src={selectedImage || ''}
        onClose={() => setSelectedImage(null)}
        alt="Screenshot"
      />
    </div>
  );
};

export default GettingStarted;
